import { parsePhoneNumberFromString } from 'libphonenumber-js/min'
import isEmail from 'validator/lib/isEmail.js'

export const BOOKING_CONTACT_LIMITS = {
  name: 120,
  email: 320,
  phone: 40,
  notes: 2_000,
} as const

export type BookingDetailField = 'name' | 'email' | 'phone' | 'notes'
export type BookingDetailErrors = Partial<Record<BookingDetailField, string>>

interface BookingDetailsInput {
  name: string
  email: string
  phone: string
  notes: string
}

interface NormalizedBookingDetails {
  name: string
  email: string
  phone?: string
  notes?: string
}

export type BookingDetailsValidation =
  | { ok: true; value: NormalizedBookingDetails }
  | { ok: false; errors: BookingDetailErrors }

const SINGLE_LINE_CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/
const MULTILINE_CONTROL_CHARACTERS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/

export function validateBookingDetails(input: BookingDetailsInput): BookingDetailsValidation {
  const errors: BookingDetailErrors = {}
  const name = input.name.trim().replace(/\s+/g, ' ')
  const email = input.email.trim().toLowerCase()
  const notes = input.notes.trim()

  if (!name) {
    errors.name = 'Please tell us who the appointment is for.'
  } else if (
    name.length > BOOKING_CONTACT_LIMITS.name
    || SINGLE_LINE_CONTROL_CHARACTERS.test(name)
  ) {
    errors.name = `Please keep the name to ${BOOKING_CONTACT_LIMITS.name} characters.`
  }

  if (
    !email
    || email.length > BOOKING_CONTACT_LIMITS.email
    || !isEmail(email, {
      allow_display_name: false,
      allow_utf8_local_part: true,
      ignore_max_length: false,
      require_tld: true,
    })
  ) {
    errors.email = 'Enter a valid email address so we can send your confirmation.'
  }

  const phoneResult = normalizeBookingPhone(input.phone)
  const phone = phoneResult.ok ? phoneResult.value : undefined
  if (!phoneResult.ok) {
    errors.phone = phoneResult.error
  }

  if (
    notes.length > BOOKING_CONTACT_LIMITS.notes
    || MULTILINE_CONTROL_CHARACTERS.test(notes)
  ) {
    errors.notes = `Please keep this to ${BOOKING_CONTACT_LIMITS.notes.toLocaleString('en-CA')} characters.`
  }

  if (Object.keys(errors).length > 0) {
    return { ok: false, errors }
  }

  return {
    ok: true,
    value: {
      name,
      email,
      phone,
      notes: notes || undefined,
    },
  }
}

export type PhoneValidation =
  | { ok: true; value?: string }
  | { ok: false; error: string }

export function normalizeBookingPhone(value: string): PhoneValidation {
  const phone = value.trim()
  if (!phone) return { ok: true }
  if (
    phone.length > BOOKING_CONTACT_LIMITS.phone
    || SINGLE_LINE_CONTROL_CHARACTERS.test(phone)
    || !phone.startsWith('+')
  ) {
    return {
      ok: false,
      error: 'Enter a valid phone number with its country code, such as +1 514 555 0134.',
    }
  }

  const parsed = parsePhoneNumberFromString(phone)
  if (!parsed?.isValid()) {
    return {
      ok: false,
      error: 'Enter a valid phone number with its country code, such as +1 514 555 0134.',
    }
  }
  return { ok: true, value: parsed.number }
}
